import { createReducer, createAction, createSelector } from 'redux-starter-kit';
import { connect } from 'react-redux';
import { isEmpty } from 'ramda';

import auth from 'consts/auth';
import { BusinessSectorType } from 'store/modules/assistant/assistant';

export enum BexioUserStatus {
  STARTED = 1,
  COLLECTING_DATA = 2,
  SUSPENDED = 3,
  CREDIT_CARD_FAILURE = 4,
}

export interface OrganizationType {
  additional_address: string | null;
  address: string | null;
  city: string | null;
  codename: string;
  country: string | null;
  currency: string | null;
  id: number;
  name: string;
  logo: string;
  stats: {
    projects: number;
    drafts: number;
  };
  drafts: number;
  projects: number;
  type: number;
  organization_role: number;
  business_sectors: BusinessSectorType[];
  business_sectors_ids?: number[];
  trusted: boolean;
  available_tokens: number;
  total_spent_tokens: number;
}
export enum UserSource {
  deeptrue = 1,
  bexio = 2,
}

export interface UserProfileDataType {
  first_name: string;
  last_name: string;
  username: string;
  phone: string | null;
  organization: string;
}

export interface UserType extends UserProfileDataType {
  created_at: string;
  email: string;
  language: string;
  organizations: OrganizationType[];
  terms_agreement: boolean;
  updated_at: string;
  uuid: string;
  role: number;
  email_confirmed: boolean;
  is_password_set: boolean;
  source: UserSource;
  bexio?: {
    status: BexioUserStatus;
    integrationUuid: string | null;
  };
}

// State
export interface AuthState {
  token: string | undefined;
  tokenChecked: boolean;
  isSignedIn: boolean;
  user: Partial<UserType>;
}

export const initialState: AuthState = {
  token: undefined,
  tokenChecked: false,
  isSignedIn: false,
  user: {
    organizations: [],
    bexio: {
      status: 1,
      integrationUuid: null,
    },
  },
};

// Actions
const SIGN_IN = 'auth/SIGN_IN';
const SIGN_OUT = 'auth/SIGN_OUT';
const SIGN_UP = 'auth/SIGN_UP';
const REFRESH_TOKEN = 'auth/REFRESH_TOKEN';
const USER_UPDATE = 'auth/USER_UPDATE';
const SET_USER_ORG = 'auth/SET_USER_ORG';
export const CLEAR_STORE = 'CLEAR_STORE';
const UPDATE_BEXIO_STATUS = 'BEXIO/UPDATE STATUS';

export const clearStore = createAction(CLEAR_STORE);
const signIn = createAction(SIGN_IN);
const signOut = createAction(SIGN_OUT);
const signUp = createAction(SIGN_UP);
const refreshToken = createAction(REFRESH_TOKEN);
const userUpdate = createAction(USER_UPDATE);
const setUserOrg = createAction(SET_USER_ORG);
const updateBexioStatus = createAction(UPDATE_BEXIO_STATUS);

export const authActions = {
  signIn,
  signUp,
  signOut: () => (dispatch: any) => {
    dispatch(signOut());
    dispatch(clearStore());
  },
  refreshToken,
  userUpdate,
  setUserOrg,
  bexio: {
    updateBexioStatus,
  },
};

// Selectors
export const authSelectors = {
  token: createSelector(['auth.token']),
  tokenChecked: createSelector(['auth.tokenChecked']),
  isSignedIn: createSelector(['auth.token'], (token) => !!token),
  userOrganizations: createSelector(['auth.user.organizations']),
  userOrganization: createSelector(
    ['auth.user.organizations'],
    (organizations: OrganizationType[]) =>
      organizations.length > 0 ? organizations[0] : undefined
  ),
  userProfileData: createSelector(['auth.user'], (user: UserType) => ({
    username: user.username,
    first_name: user.first_name,
    last_name: user.last_name,
    phone: user.phone,
    language: user.language,
    organization:
      user.organizations.length > 0 ? user.organizations[0].name : '-',
  })),
  userProfile: createSelector(['auth.user']),
  isDeepTrueAdmin: createSelector(
    ['auth.user.role'],
    (role) => role === auth.ROLES.DEEPTRUE_ADMIN
  ),
  isOrganizationAdmin: createSelector(['auth.user'], (user: UserType) => {
    return user.organizations.length > 0
      ? user.organizations[0].organization_role === auth.ORGANIZATION_ROLE.ADMIN
      : false;
  }),
  isOrganizationGuest: createSelector(['auth.user'], (user: UserType) => {
    return user.organizations.length > 0
      ? user.organizations[0].organization_role === auth.ORGANIZATION_ROLE.GUEST
      : false;
  }),
  userDisplayName: createSelector(['auth.user'], (user) =>
    !isEmpty(user.first_name)
      ? `${user.first_name} ${user.last_name}`
      : user.username
  ),
  isBexioUser: createSelector(['auth.user'], (user: UserType) => {
    return user.source === UserSource.bexio;
  }),
  bexioUserStatus: createSelector(['auth.user'], (user: UserType) => {
    return user.bexio?.status;
  }),
  bexioIntegrationUuid: createSelector(['auth.user'], (user: UserType) => {
    return user.bexio?.integrationUuid;
  }),
};

// Connect
export const authConnect = {
  withIsSignedIn: connect((state) => ({
    isSignedIn: authSelectors.isSignedIn(state),
  })),
  withIsDeepTrueAdmin: connect((state) => ({
    isDeepTrueAdmin: authSelectors.isDeepTrueAdmin(state),
  })),
};

// Reducers
export default createReducer(initialState, {
  [SIGN_IN]: (state, action) => {
    const { token, user } = action.payload;
    const isBexioUser = user.role === UserSource.bexio;
    state.token = token;

    if (isBexioUser) {
      state.user = {
        ...user,
        bexio: {
          status: user.bexio_integration_status,
          integrationUuid: user.bexio_integration_uuid,
        },
      };
    } else {
      state.user = user;
    }
  },
  [SIGN_UP]: (state, action) => {
    state.token = action.payload.token;
    state.user = action.payload.user;
  },
  [SIGN_OUT]: (state) => {
    state.token = initialState.token;
    state.user = initialState.user;
  },
  [USER_UPDATE]: (state, action) => {
    state.user = action.payload;
  },
  [SET_USER_ORG]: (state, action) => {
    if (typeof state.user.organizations !== 'undefined') {
      state.user.organizations[0] = {
        ...state.user.organizations[0],
        ...action.payload,
      };
    }
  },
  [UPDATE_BEXIO_STATUS]: (state, { payload }) => {
    const { status } = payload;

    if (state.user.bexio) {
      state.user = {
        ...state.user,
        bexio: {
          ...state.user.bexio,
          status: status,
        },
      };
    }
  },
});
